import { DialogWithForm } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { UpdateReferralDataAddPerformance } from 'components/referrals/dialogs/UpdateReferralDataAddPerformance';
import { UpdateReferralDataPerformance } from 'components/referrals/dialogs/UpdateReferralDataPerformance';
import { handleError } from 'services/Error';
import { getAddableValueCases } from 'services/valueCaseHelpers';
import {
    useGetAdHocValueCaseEligibilityQuery,
    useGetValueCasesQuery,
} from 'store/api/performanceApi';
import { fetchReferralDetails } from 'store/referrals/referralsSlice';

const Title = 'Update Referral Data';
const AsoReferralMessage =
    'Ad-hoc value cases cannot be added to Administrative Services Only referrals.';
const AsoStatusUnverifiedMessage =
    'Ad-hoc value cases cannot be added until Administrative Services Only status can be verified.';

const UpdateTypes = {
    ADD_PERFORMANCE: 'Add Performance Requirement',
    REMOVE_PERFORMANCE: 'Performance Requirement(s) and Associated Worklist Task(s)',
    PRESCRIBER: 'Prescriber',
};
const UpdateTypeComponentMap = {
    [UpdateTypes.ADD_PERFORMANCE]: UpdateReferralDataAddPerformance,
    [UpdateTypes.REMOVE_PERFORMANCE]: UpdateReferralDataPerformance,
};

export const UpdateReferralDataDialog = ({
    isOpen,
    onClose,
    referralDetails = undefined,
    memberDetails = undefined,
    performanceEntries = undefined,
    username,
}) => {
    const dispatch = useDispatch();
    const { showAddAdHocValueCaseAgora = false } = useFlags() ?? {};
    const { payerId, referralId } = referralDetails ?? {};
    const { data: valueCases } = useGetValueCasesQuery(undefined, {
        skip: !showAddAdHocValueCaseAgora,
    });
    const { data: eligibility, isError: eligibilityReadFailed } =
        useGetAdHocValueCaseEligibilityQuery(
            { referralId },
            { skip: !showAddAdHocValueCaseAgora || !referralId },
        );
    const referralIsAso = eligibility?.reason === 'aso';
    const asoStatusUnverified = eligibility?.reason === 'lookup_failed' || eligibilityReadFailed;
    // Only offer Add once the backend confirms it, so it never flashes in while the read is in flight.
    const adHocAllowed = eligibility?.allowed === true;

    const [selectedUpdateType, setSelectedUpdateType] = useState();
    const SelectedUpdateTypeComponent = UpdateTypeComponentMap[selectedUpdateType];

    const updateTypeOptions = useMemo(() => {
        const options = [];

        if (
            showAddAdHocValueCaseAgora &&
            adHocAllowed &&
            getAddableValueCases({ valueCases, referralDetails, performanceEntries }).length > 0
        ) {
            options.push(UpdateTypes.ADD_PERFORMANCE);
        }

        if (
            (performanceEntries ?? []).filter(
                ({ status }) => status === 'not-started' || status === 'pending',
            ).length > 0
        ) {
            options.unshift(UpdateTypes.REMOVE_PERFORMANCE);
        }

        return options;
    }, [adHocAllowed, performanceEntries, referralDetails, showAddAdHocValueCaseAgora, valueCases]);

    return (
        <Formik
            initialValues={{
                updateType: selectedUpdateType,
                ...SelectedUpdateTypeComponent?.initialValues({ referralDetails, memberDetails }),
            }}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                updateType: Yup.string().required(validationStrings.requiredField.errorMessage),
                ...SelectedUpdateTypeComponent?.validationSchema,
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await SelectedUpdateTypeComponent?.onSubmit({
                        referralDetails,
                        memberDetails,
                        values,
                        username,
                    });

                    await dispatch(fetchReferralDetails({ payerId, referralId }));

                    SuccessToast({ message: 'Referral data updated successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update referral data. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Update Referral"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            {(referralIsAso || asoStatusUnverified) && (
                                <div className="margin__bottom--medium">
                                    <InlineMessage
                                        intent={InlineMessage.Intent.INFO}
                                        wrapText
                                        label={
                                            referralIsAso
                                                ? AsoReferralMessage
                                                : AsoStatusUnverifiedMessage
                                        }
                                    />
                                </div>
                            )}
                            <SuggestSingleSelectFormik
                                id="updateType"
                                label="What Do You Need To Update?"
                                required
                                values={updateTypeOptions}
                                onChange={setSelectedUpdateType}
                            />
                            {SelectedUpdateTypeComponent && (
                                <SelectedUpdateTypeComponent
                                    referralDetails={referralDetails}
                                    memberDetails={memberDetails}
                                    performanceEntries={performanceEntries}
                                />
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

UpdateReferralDataDialog.Title = Title;

UpdateReferralDataDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    referralDetails: PropTypes.shape({
        payerId: PropTypes.string.isRequired,
        referralId: PropTypes.string.isRequired,
        treatmentId: PropTypes.string.isRequired,
        isBrand: PropTypes.bool,
    }),
    memberDetails: PropTypes.shape({}),
    performanceEntries: PropTypes.arrayOf(PropTypes.shape({})),
    username: PropTypes.string.isRequired,
};
